import { Link } from '@reach/router';
import React, { useContext, useState } from 'react';
import { useMutation } from 'react-apollo-hooks';
import { toast } from 'react-toastify';
import { NotebookContext } from '../context/NotebookContext';
import { subjectBySlugQuery } from '../queries/subjects';
import { trackerCreateOrUpdateMutation } from '../queries/trackers';
import {
  TrackerCreateOrUpdateMutation,
  TrackerCreateOrUpdateMutationVariables
} from '../queries/__generated__/TrackerCreateOrUpdateMutation';
import { TrackerType as APITrackerType } from '../__generated__/globalTypes';
import ErrorMessages from './ErrorMessages';
import { Input } from './forms';
import TrackerTypeSelect from './forms/TrackerTypeSelect';
import { Tracker } from './settings/group/types';
import { Modal } from './ui';

interface EditTrackerModalProps {
  closeModal: () => void;
  tracker: Tracker;
}

const EditTrackerModal: React.FC<EditTrackerModalProps> = ({ closeModal, tracker }) => {
  const [trackerName, setTrackerName] = useState(tracker.name);
  const [trackerType, setTrackerType] = useState<TrackerType>(tracker.type);
  const [errors, setErrors] = useState<{ message: string }[]>([]);
  const { notebook, reloadNotebook } = useContext(NotebookContext);

  const editTrackerMutation = useMutation<
    TrackerCreateOrUpdateMutation,
    TrackerCreateOrUpdateMutationVariables
  >(trackerCreateOrUpdateMutation, {
    variables: {
      input: {
        notebookId: notebook.id,
        id: tracker.id,
        name: trackerName,
        type: trackerType as APITrackerType
      }
    }
  });

  async function updateTracker() {
    const { data } = await editTrackerMutation();
    const trackerRes = data!.createOrUpdateTracker.tracker;
    const errors = data!.createOrUpdateTracker.errors;
    if (errors) {
      setErrors(errors);
      return;
    }

    toast(`Tracker Saved: ${trackerRes!.name}`, { type: 'success' });
    reloadNotebook();
    closeModal();
  }

  return (
    <Modal closeModal={closeModal}>
      <div className="pa4 navy">
        <div className="tc">
          <h1 className="f4 fw6 mb2">Edit Tracker</h1>
        </div>

        <div className="mb3 tc">
          <ErrorMessages errors={errors} />
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            if (trackerName) {
              updateTracker();
            }
          }}
        >
          <Input
            value={trackerName}
            onChange={e => setTrackerName(e.target.value)}
            wrapperClassName="mb2"
            placeholder="Tracker name..."
            data-testid="tracker-name-input"
          />
          <TrackerTypeSelect
            selectedType={trackerType as TrackerType}
            setTrackerType={setTrackerType}
            disabled={true}
          />
        </form>

        <div className="tc">
          <Link
            to={`/notebook/tracker/${tracker.id}/delete`}
            className="f6 blue hover-navy dib center"
          >
            Delete Tracker
          </Link>
        </div>
      </div>

      <div className="flex bt b--light-gray navy">
        <button
          className="w-50 br b--light-gray f6 pa3 tc blue hover-navy"
          onClick={closeModal}
          data-testid="cancel-button"
        >
          Cancel
        </button>

        <button
          className="w-50 f6 pa3 tc blue hover-navy"
          onClick={updateTracker}
          disabled={!trackerName}
          data-testid="save-tracker-button"
        >
          Save Tracker
        </button>
      </div>
    </Modal>
  );
};

export default EditTrackerModal;
